import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiEdit3, FiUploadCloud } from "react-icons/fi";
import { listContentVersions } from "../ContentVersions/contentVersionsClient";
import { getContentHistory } from "../ContentHistory/contentHistoryClient";
import { getUserDirectory, type DirectoryEntry } from "../Account/accountClient";
import { usePermissions } from "../../Auth/PermissionsContext";
import { useRealtimeEvent } from "../../Realtime/RealtimeProvider";
import { parseApiDate } from "../../Helper/dates";
import { UserAvatar } from "../UserAvatar";

/**
 * First banner of the content editor: two halves at 50/50 : the LAST EDITOR (newest saved version's
 * author) and the LAST PUBLISHER (author of the newest `content-deploy-production` log entry). Each shows
 * pseudo + profile picture + a clickable link to /account/{id} when the author resolves in the directory.
 * A raw email/sub that doesn't resolve shows plain (no link); "SYSTEM"/empty renders as « Système ».
 */

const PRODUCTION_DEPLOY_TYPE = "content-deploy-production";

interface Party {
	name: string;
	when?: string | null;
}

const isSystem = (raw?: string | null): boolean => {
	const s = (raw ?? "").trim();
	return s.length === 0 || s.toUpperCase() === "SYSTEM";
};

const displayName = (raw?: string | null): string => (isSystem(raw) ? "Système" : (raw ?? "").trim());

const formatWhen = (iso?: string | null): string => {
	const d = parseApiDate(iso ?? undefined);
	return d ? d.toLocaleString("fr-FR") : "";
};

const Half: React.FC<{
	label: string;
	icon: React.ReactNode;
	party: Party | null;
	resolve: (nameOrEmail?: string | null) => DirectoryEntry | null;
	emptyLabel: string;
}> = ({ label, icon, party, resolve, emptyLabel }) => {
	const raw = party?.name;
	const entry = raw && !isSystem(raw) ? resolve(raw) : null;
	const name = displayName(raw);
	const id = entry?.id ?? null;
	const avatar = <UserAvatar name={name} id={id ?? undefined} avatarKey={entry?.avatarKey} pictureUrl={entry?.picture} sizeClass="h-10 w-10" textClass="text-sm" />;
	return (
		<div className="flex min-w-0 items-center gap-3 px-4 py-3">
			<div className="flex shrink-0 items-center gap-2 text-slate-400">
				<span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100">{icon}</span>
			</div>
			{party ? (
				<div className="flex min-w-0 items-center gap-3">
					{avatar}
					<div className="min-w-0">
						<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
						{id ? (
							<Link to={`/account/${id}`} className="block truncate text-sm font-semibold text-slate-800 hover:text-sky-700 hover:underline">
								{name}
							</Link>
						) : (
							<p className="block truncate text-sm font-semibold text-slate-800">{name}</p>
						)}
						{party.when && <p className="truncate text-[11px] text-slate-400">{formatWhen(party.when)}</p>}
					</div>
				</div>
			) : (
				<div className="min-w-0">
					<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
					<p className="text-sm text-slate-400">{emptyLabel}</p>
				</div>
			)}
		</div>
	);
};

export const EditorAuthorshipBanner: React.FC<{ contentId?: string | null; className?: string }> = ({ contentId, className = "" }) => {
	const { can } = usePermissions();
	const canLinkProfiles = can("feature:profile:read");
	const [lastEditor, setLastEditor] = useState<Party | null>(null);
	const [lastPublisher, setLastPublisher] = useState<Party | null>(null);
	const [loaded, setLoaded] = useState(false);
	const [directory, setDirectory] = useState<DirectoryEntry[]>([]);

	const load = useCallback(async () => {
		if (!contentId) return;
		try {
			const [versions, history] = await Promise.all([
				listContentVersions(contentId, 1, 1).catch(() => null),
				getContentHistory(contentId, 50).catch(() => [])
			]);
			const v = versions?.items?.[0];
			setLastEditor(v ? { name: v.authorEmail, when: v.createdAt } : null);
			const deploy = (history ?? []).find((e) => (e.type ?? "").toLowerCase() === PRODUCTION_DEPLOY_TYPE);
			setLastPublisher(deploy ? { name: deploy.user, when: deploy.updatedAt ?? deploy.createdAt } : null);
		} finally {
			setLoaded(true);
		}
	}, [contentId]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		let cancelled = false;
		if (!canLinkProfiles) {
			setDirectory([]);
			return;
		}
		getUserDirectory().then((d) => !cancelled && setDirectory(d)).catch(() => !cancelled && setDirectory([]));
		return () => {
			cancelled = true;
		};
	}, [canLinkProfiles]);

	// Live refresh: a save updates the last editor; a production deploy updates the last publisher.
	useRealtimeEvent("content-saved", (evt: any) => {
		if (contentId && evt?.id === contentId) load();
	});
	useRealtimeEvent("deploy", (evt: any) => {
		if (contentId && (!evt?.phase || evt.phase === "finish")) load();
	});

	const resolve = useMemo(() => {
		const byName = new Map<string, DirectoryEntry>();
		const byEmail = new Map<string, DirectoryEntry>();
		for (const e of directory) {
			if (e.name) byName.set(e.name.trim().toLowerCase(), e);
			if (e.email) byEmail.set(e.email.trim().toLowerCase(), e);
		}
		return (nameOrEmail?: string | null): DirectoryEntry | null => {
			const k = (nameOrEmail ?? "").trim().toLowerCase();
			if (!k) return null;
			return byName.get(k) ?? byEmail.get(k) ?? null;
		};
	}, [directory]);

	if (!contentId) return null;
	// Nothing to show until we have at least an editor or a publisher.
	if (loaded && !lastEditor && !lastPublisher) return null;

	return (
		<div className={`grid grid-cols-1 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:grid-cols-2 sm:divide-x sm:divide-y-0 ${className}`}>
			<Half label="Dernier éditeur" icon={<FiEdit3 className="h-3.5 w-3.5" />} party={lastEditor} resolve={resolve} emptyLabel="Aucune modification" />
			<Half label="Dernier publicateur" icon={<FiUploadCloud className="h-3.5 w-3.5" />} party={lastPublisher} resolve={resolve} emptyLabel="Jamais publié en production" />
		</div>
	);
};

export default EditorAuthorshipBanner;
